import { C, type DemoTermElement } from "./shared";

const INSTRUMENTS = ["KICK", "SNRE", "HHAT", "CLAP"] as const;
const STEP_COUNT = 16;
const PATTERN_NAMES = ["A", "B", "C", "D"];

const ON_CHAR = "\u2588";
const OFF_CHAR = "\u2591";

const COL = {
  on: C.green,
  off: "#2a2a3a",
  head: "#0a0a0a",
  headBg: C.green,
  headOff: "#0a0a0a",
  headOffBg: "#3a3a4a",
  label: C.brightBlack,
  accent: C.green,
};

const DEFAULT_PATTERNS: boolean[][][] = [
  [
    [true, false, false, false, true, false, false, false, true, false, false, false, true, false, false, false],
    [false, false, false, false, true, false, false, false, false, false, false, false, true, false, false, false],
    [true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true],
    [false, false, false, false, true, false, false, false, false, false, false, false, true, false, false, true],
  ],
  Array.from({ length: 4 }, () => Array(16).fill(false)),
  Array.from({ length: 4 }, () => Array(16).fill(false)),
  Array.from({ length: 4 }, () => Array(16).fill(false)),
];

type AudioEngine = {
  ctx: AudioContext;
  analyser: AnalyserNode;
  noiseBuffer: AudioBuffer;
};

type DrumDom = {
  patternButtons: HTMLButtonElement[];
  stepButtons: HTMLButtonElement[][];
  playButton: HTMLButtonElement;
  bpmDownButton: HTMLButtonElement;
  bpmUpButton: HTMLButtonElement;
  bpmText: HTMLParagraphElement;
  waveformFigure: HTMLElement;
  statusFooter: HTMLElement;
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      node.setAttribute(key, value);
    }
  }
  return node;
}

function setButtonVisual(button: HTMLButtonElement, text: string, color: string, bg?: string): void {
  button.textContent = text;
  button.setAttribute("color", color);
  if (bg) button.setAttribute("bg", bg);
  else button.removeAttribute("bg");
}

function clonePatterns(source: boolean[][][]): boolean[][][] {
  return source.map((pattern) => pattern.map((row) => [...row]));
}

function createNoiseBuffer(ctx: AudioContext): AudioBuffer {
  const size = ctx.sampleRate * 0.5;
  const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let index = 0; index < size; index++) {
    channel[index] = Math.random() * 2 - 1;
  }
  return buffer;
}

function initAudio(): AudioEngine {
  const ctx = new AudioContext();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 64;
  analyser.smoothingTimeConstant = 0.7;
  analyser.connect(ctx.destination);
  const noiseBuffer = createNoiseBuffer(ctx);
  return { ctx, analyser, noiseBuffer };
}

function playKick(engine: AudioEngine, time: number): void {
  const osc = engine.ctx.createOscillator();
  const gain = engine.ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150, time);
  osc.frequency.exponentialRampToValueAtTime(40, time + 0.1);
  gain.gain.setValueAtTime(0.8, time);
  gain.gain.exponentialRampToValueAtTime(0.01, time + 0.3);
  osc.connect(gain).connect(engine.analyser);
  osc.start(time);
  osc.stop(time + 0.3);
}

function playSnare(engine: AudioEngine, time: number): void {
  const noise = engine.ctx.createBufferSource();
  noise.buffer = engine.noiseBuffer;
  const filter = engine.ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 4000;
  const noiseGain = engine.ctx.createGain();
  noiseGain.gain.setValueAtTime(0.5, time);
  noiseGain.gain.exponentialRampToValueAtTime(0.01, time + 0.15);
  noise.connect(filter).connect(noiseGain).connect(engine.analyser);
  noise.start(time);
  noise.stop(time + 0.15);

  const osc = engine.ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.value = 200;
  const oscGain = engine.ctx.createGain();
  oscGain.gain.setValueAtTime(0.4, time);
  oscGain.gain.exponentialRampToValueAtTime(0.01, time + 0.1);
  osc.connect(oscGain).connect(engine.analyser);
  osc.start(time);
  osc.stop(time + 0.1);
}

function playHat(engine: AudioEngine, time: number): void {
  const noise = engine.ctx.createBufferSource();
  noise.buffer = engine.noiseBuffer;
  const filter = engine.ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 7000;
  const gain = engine.ctx.createGain();
  gain.gain.setValueAtTime(0.25, time);
  gain.gain.exponentialRampToValueAtTime(0.01, time + 0.05);
  noise.connect(filter).connect(gain).connect(engine.analyser);
  noise.start(time);
  noise.stop(time + 0.05);
}

function playClap(engine: AudioEngine, time: number): void {
  const noise = engine.ctx.createBufferSource();
  noise.buffer = engine.noiseBuffer;
  const filter = engine.ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 1200;
  filter.Q.value = 0.5;
  const gain = engine.ctx.createGain();
  gain.gain.setValueAtTime(0.6, time);
  gain.gain.exponentialRampToValueAtTime(0.1, time + 0.02);
  gain.gain.setValueAtTime(0.5, time + 0.025);
  gain.gain.exponentialRampToValueAtTime(0.01, time + 0.15);
  noise.connect(filter).connect(gain).connect(engine.analyser);
  noise.start(time);
  noise.stop(time + 0.15);
}

const PLAYERS = [playKick, playSnare, playHat, playClap] as const;

function readWaveform(engine: AudioEngine | null): number[] {
  if (!engine) return Array(24).fill(0) as number[];
  const buffer = new Uint8Array(engine.analyser.frequencyBinCount);
  engine.analyser.getByteTimeDomainData(buffer);
  const samples: number[] = [];
  const step = Math.max(1, Math.floor(buffer.length / 24));
  for (let index = 0; index < 24; index++) {
    samples.push(Math.abs((buffer[index * step]! - 128) / 128) * 9);
  }
  return samples;
}

function updateStepButton(
  button: HTMLButtonElement,
  on: boolean,
  stepIndex: number,
  currentStep: number,
): void {
  const isHead = stepIndex === currentStep;
  const color = isHead ? (on ? COL.head : COL.headOff) : on ? COL.on : COL.off;
  const bg = isHead ? (on ? COL.headBg : COL.headOffBg) : undefined;
  setButtonVisual(button, on ? ON_CHAR : OFF_CHAR, color, bg);
}

function createDrumDom(
  host: HTMLElement,
  onPattern: (index: number) => void,
  onToggleCell: (instrument: number, step: number) => void,
  onTogglePlay: () => void,
  onBpmDown: () => void,
  onBpmUp: () => void,
  signal: AbortSignal,
): DrumDom {
  const root = el("section", { gap: "1" });

  const patternNav = el("nav", { gap: "1" });
  const patternButtons: HTMLButtonElement[] = [];
  for (let index = 0; index < PATTERN_NAMES.length; index++) {
    const button = el("button");
    button.textContent = PATTERN_NAMES[index]!;
    button.addEventListener("click", () => onPattern(index), { signal });
    patternButtons.push(button);
    patternNav.append(button);
  }

  const rowsSection = el("section", { gap: "1" });
  const stepButtons: HTMLButtonElement[][] = [];
  for (let instrument = 0; instrument < INSTRUMENTS.length; instrument++) {
    const row = el("section", { gap: "0" });
    const label = el("p", { color: COL.label });
    label.textContent = INSTRUMENTS[instrument]!;

    const top = el("nav", { gap: "0" });
    const bottom = el("nav", { gap: "0" });
    const rowButtons: HTMLButtonElement[] = [];
    for (let step = 0; step < STEP_COUNT; step++) {
      const button = el("button");
      button.addEventListener("click", () => onToggleCell(instrument, step), { signal });
      rowButtons.push(button);
      if (step < 8) top.append(button);
      else bottom.append(button);
    }
    stepButtons.push(rowButtons);
    row.append(label, top, bottom);
    rowsSection.append(row);
  }

  const controlNav = el("nav", { gap: "1" });
  const playButton = el("button");
  playButton.addEventListener("click", onTogglePlay, { signal });
  const bpmDownButton = el("button");
  bpmDownButton.textContent = "-";
  bpmDownButton.addEventListener("click", onBpmDown, { signal });
  const bpmUpButton = el("button");
  bpmUpButton.textContent = "+";
  bpmUpButton.addEventListener("click", onBpmUp, { signal });
  const bpmText = el("p", { color: C.white });
  controlNav.append(playButton, bpmDownButton, bpmUpButton, bpmText);

  const waveformFigure = el("figure", {
    height: "3",
    fill: "",
    color: COL.accent,
  });
  const statusFooter = el("footer", {
    bg: "#1a1a2e",
    color: C.white,
  });

  root.append(patternNav, rowsSection, controlNav, waveformFigure, statusFooter);
  host.replaceChildren(root);

  return {
    patternButtons,
    stepButtons,
    playButton,
    bpmDownButton,
    bpmUpButton,
    bpmText,
    waveformFigure,
    statusFooter,
  };
}

export function mountDrumMachine(term: DemoTermElement): () => void {
  const host = term as unknown as HTMLElement;
  host.removeAttribute("source");
  host.removeAttribute("source-format");

  const patterns = clonePatterns(DEFAULT_PATTERNS);
  let activePattern = 0;
  let playing = false;
  let bpm = 120;
  let playStartMs = 0;
  let lastStep = -1;
  let renderedStep = -2;
  let engine: AudioEngine | null = null;

  const currentStep = (): number => {
    if (!playing) return -1;
    const stepMs = 60000 / bpm / 4;
    return Math.floor((Date.now() - playStartMs) / stepMs) % STEP_COUNT;
  };

  const triggerStep = (step: number) => {
    if (!playing || !engine || step < 0 || step === lastStep) return;
    lastStep = step;
    const pattern = patterns[activePattern]!;
    const when = engine.ctx.currentTime;

    for (let index = 0; index < INSTRUMENTS.length; index++) {
      if (pattern[index]?.[step]) {
        PLAYERS[index]?.(engine, when);
      }
    }
  };

  const toggleCell = (instrument: number, step: number): void => {
    const row = patterns[activePattern]?.[instrument];
    if (!row) throw new Error(`Missing row for instrument ${instrument}`);
    row[step] = !row[step];
    render();
  };

  const setPattern = (index: number): void => {
    activePattern = Math.max(0, Math.min(PATTERN_NAMES.length - 1, index));
    lastStep = -1;
    render();
  };

  const togglePlay = (): void => {
    if (!playing) {
      if (!engine) engine = initAudio();
      void engine.ctx.resume();
      playStartMs = Date.now();
      lastStep = -1;
      playing = true;
      render();
      return;
    }

    playing = false;
    lastStep = -1;
    render();
  };

  const adjustBpm = (delta: number): void => {
    bpm = Math.max(60, Math.min(200, bpm + delta));
    playStartMs = Date.now();
    lastStep = -1;
    render();
  };

  const listeners = new AbortController();
  const dom = createDrumDom(
    host,
    setPattern,
    toggleCell,
    togglePlay,
    () => adjustBpm(-5),
    () => adjustBpm(5),
    listeners.signal,
  );

  const render = (): void => {
    const step = currentStep();
    const pattern = patterns[activePattern]!;
    const waveform = readWaveform(engine);

    for (let index = 0; index < dom.patternButtons.length; index++) {
      const button = dom.patternButtons[index]!;
      const active = index === activePattern;
      setButtonVisual(button, PATTERN_NAMES[index]!, active ? COL.head : COL.label, active ? COL.headBg : undefined);
    }

    for (let instrument = 0; instrument < dom.stepButtons.length; instrument++) {
      const rowButtons = dom.stepButtons[instrument]!;
      for (let cell = 0; cell < rowButtons.length; cell++) {
        updateStepButton(rowButtons[cell]!, pattern[instrument]![cell]!, cell, step);
      }
    }

    setButtonVisual(dom.playButton, playing ? "\u25a0 STOP" : "\u25b6 PLAY", COL.accent);
    setButtonVisual(dom.bpmDownButton, "-", COL.label);
    setButtonVisual(dom.bpmUpButton, "+", COL.label);
    dom.bpmText.textContent = `${bpm} bpm`;

    dom.waveformFigure.setAttribute("values", waveform.map((value) => value.toFixed(2)).join(","));

    const stepDisplay = step >= 0 ? `${step + 1}` : "--";
    dom.statusFooter.setAttribute(
      "left",
      ` ${playing ? "\u25cf" : "\u25cb"} ${playing ? "playing" : "stopped"} \u2502 pattern ${PATTERN_NAMES[activePattern]} \u2502 step ${stepDisplay}/${STEP_COUNT}`,
    );
    dom.statusFooter.setAttribute("right", `${bpm} bpm `);

    renderedStep = step;
  };

  render();

  const tickTimer = setInterval(() => {
    if (!playing) return;
    const step = currentStep();
    triggerStep(step);
    if (step === renderedStep) return;
    render();
  }, 25);

  return () => {
    listeners.abort();
    clearInterval(tickTimer);
    if (engine) {
      void engine.ctx.close();
      engine = null;
    }
  };
}
